import { useRouter } from 'expo-router';
import { ExternalLink, MapPin } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Image, Linking, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { SvgXml } from 'react-native-svg';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { MAP_MARKER_SVG_XML } from '@/generated/map-marker-svg-data';
import { getApiKey } from '@/lib/api-keys';
import {
  isProxyConfigured,
  proxyEndpoint,
  SUPABASE_ANON_KEY,
} from '@/lib/proxy';
import { tryConsume } from '@/lib/quota';

/**
 * Map preview of the property's lat/lon. Uses Google Static Maps when the
 * user has a Google key (the same one that powers Places autocomplete);
 * falls back to a tappable card that opens OpenStreetMap in the browser.
 *
 * Designed for the My Home screen — wraps the whole thing in a Pressable so
 * tapping anywhere opens the full map in the platform's default mapping app.
 */
export function StaticMapCard({
  lat,
  lon,
  address,
  height = 180,
}: {
  lat: number | null;
  lon: number | null;
  address?: string | null;
  height?: number;
}) {
  const router = useRouter();
  const [key, setKey] = useState<string | null>(null);
  const [budgetOk, setBudgetOk] = useState<boolean | null>(null);
  // True when there's no user key but our server proxy is configured. On
  // native, the <Image> attaches the anon key as a header. On web, the
  // anon key rides in the URL as an `apikey` query param (Supabase Edge
  // Functions accept that form too), since browsers can't attach custom
  // headers to <img> requests.
  const useProxy = !key && isProxyConfigured();

  useEffect(() => {
    (async () => {
      const k = await getApiKey('google_places');
      setKey(k);
      if ((k || isProxyConfigured()) && lat != null && lon != null) {
        // One consume per render of this component instance. Cached image
        // by browser/RN means the same URL won't actually hit the API
        // every time, but the budget is intentionally conservative.
        const res = await tryConsume('static_maps');
        setBudgetOk(res.ok);
      } else {
        setBudgetOk(true);
      }
    })();
  }, [lat, lon]);

  if (lat == null || lon == null) {
    return (
      <Pressable
        onPress={() => router.push('/profile-setup' as never)}
        style={({ pressed }) => [styles.emptyCard, pressed && { opacity: 0.9 }]}>
        <View style={styles.emptyIcon}>
          <MapPin size={20} color={Palette.forest} strokeWidth={1.8} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.emptyTitle}>Add your address</Text>
          <Text style={styles.emptySub}>
            Tap to add it in the Tune walkthrough — unlocks the map preview here.
          </Text>
        </View>
      </Pressable>
    );
  }

  const openInMaps = () => {
    const label = address ? encodeURIComponent(address) : `${lat},${lon}`;
    const url = Platform.select({
      ios: `http://maps.apple.com/?q=${label}&ll=${lat},${lon}`,
      android: `geo:${lat},${lon}?q=${label}`,
      default: `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`,
    });
    if (url) Linking.openURL(url).catch(() => {});
  };

  return (
    <Pressable
      onPress={openInMaps}
      style={({ pressed }) => [styles.card, pressed && { opacity: 0.92 }]}>
      {(key || useProxy) && budgetOk !== false ? (
        <View style={{ height }}>
          <Image
            source={
              key
                ? { uri: googleStaticMapUrl(lat, lon, key, height) }
                : Platform.OS === 'web'
                ? {
                    // Browsers can't set custom headers on <img>, so the anon
                    // key has to be in the URL.
                    uri: proxyStaticMapUrl(lat, lon, height, /* withKey */ true),
                  }
                : {
                    uri: proxyStaticMapUrl(lat, lon, height),
                    headers: {
                      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
                      apikey: SUPABASE_ANON_KEY,
                    },
                  }
            }
            style={[styles.image, { height }]}
            accessible={false}
          />
          {/* Homestead-icon marker overlaid at the exact center of the
              static map. The map URL has no `markers=` param (see
              googleStaticMapUrl) so there's no default red Google pin
              fighting for the same pixels. The center of a centered
              static map IS the property's lat/lon, so an absolute
              positioned icon lands precisely on the address. */}
          <View pointerEvents="none" style={styles.markerOverlay}>
            <SvgXml xml={MAP_MARKER_SVG_XML} width={44} height={44} />
          </View>
        </View>
      ) : (
        <FallbackTile lat={lat} lon={lon} height={height} />
      )}
      <View style={styles.footer}>
        <MapPin size={14} color={Palette.forest} strokeWidth={2} />
        <Text style={styles.address} numberOfLines={1}>
          {address ?? `${lat.toFixed(4)}, ${lon.toFixed(4)}`}
        </Text>
        <ExternalLink size={14} color={Palette.inkFaint} strokeWidth={1.8} />
      </View>
    </Pressable>
  );
}

function FallbackTile({ lat, lon, height }: { lat: number; lon: number; height: number }) {
  return (
    <View style={[styles.fallback, { height }]}>
      <MapPin size={32} color={Palette.forest} strokeWidth={1.6} />
      <Text style={styles.fallbackText}>
        Tap to open this address in OpenStreetMap.
      </Text>
      <Text style={styles.fallbackCoord}>
        {lat.toFixed(4)}, {lon.toFixed(4)}
      </Text>
    </View>
  );
}

function googleStaticMapUrl(lat: number, lon: number, key: string, height: number): string {
  // 600x{h} is enough for a 440-wide column at @2x. We deliberately
  // OMIT the `markers=` param — the StaticMapCard component overlays
  // our homestead-icon SVG at the dead center of the map, which IS
  // the property's lat/lon (because center=lat,lon). Adding a Google
  // red pin here would just compete with our overlay.
  const params = new URLSearchParams({
    center: `${lat},${lon}`,
    zoom: '16',
    size: `600x${Math.round(height * 2)}`,
    scale: '2',
    maptype: 'roadmap',
    style: 'feature:poi|visibility:off',
    key,
  });
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

function proxyStaticMapUrl(
  lat: number,
  lon: number,
  height: number,
  withKey = false
): string {
  // Same query shape, minus `key` — the edge function injects it server-side.
  // When `withKey` is true (web path), also append the Supabase anon key so
  // the request authenticates without relying on request headers.
  const params = new URLSearchParams({
    center: `${lat},${lon}`,
    zoom: '16',
    size: `600x${Math.round(height * 2)}`,
    scale: '2',
    maptype: 'roadmap',
    // No `markers=` — the StaticMapCard overlays our homestead-icon SVG
    // at the dead center of the map (which is the lat/lon). See the
    // googleStaticMapUrl twin for the matching comment.
    style: 'feature:poi|visibility:off',
  });
  if (withKey && SUPABASE_ANON_KEY) {
    params.set('apikey', SUPABASE_ANON_KEY);
  }
  return `${proxyEndpoint('/static-map')}?${params.toString()}`;
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    backgroundColor: Palette.card,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Palette.line,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    backgroundColor: '#E8DFCB',
  },
  // Center-pin overlay for the homestead-icon marker. The icon is 44px,
  // so we nudge the position up by ~22px (half the icon height) so its
  // visual "anchor" lands on the lat/lon. The marker icon is square,
  // unlike a teardrop pin — centering by the icon's center looks
  // right for an at-this-address indicator.
  markerOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: Palette.line,
  },
  address: {
    flex: 1,
    fontFamily: Fonts.sansSemibold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  emptyCard: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Palette.card,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Palette.line,
    padding: Spacing.four,
  },
  emptyIcon: {
    width: 40, height: 40, borderRadius: 12,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  emptyTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  emptySub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft, lineHeight: 16,
  },
  fallback: {
    alignItems: 'center', justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 24,
    backgroundColor: '#EAF0EA',
  },
  fallbackText: {
    fontFamily: Fonts.sansSemibold, fontSize: 13.5, color: Palette.ink,
    textAlign: 'center', lineHeight: 17, marginTop: 2,
  },
  fallbackSub: {
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    textAlign: 'center',
  },
  fallbackCoord: {
    marginTop: 4,
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.4,
  },
});
